export type BetaReadCompensationType = 'none' | 'points' | 'payment';
export type BetaReadCompensationScope = 'book' | 'chapter';
export type BetaReaderApplicationStatus = 'pending' | 'approved' | 'rejected';
export type BetaReaderSignupSource = 'invite' | 'public_application';

export interface BookBetaReadProgramConfig {
  enabled: boolean;
  instructions: string;
  incentives: string;
  compensationType: BetaReadCompensationType;
  compensationScope: BetaReadCompensationScope;
  compensationAmount: number;
  maxBetaReaders: number | null;
  sessionStartsAt: string | null;
  sessionEndsAt: string | null;
}

export const DEFAULT_BETA_READ_PROGRAM: BookBetaReadProgramConfig = {
  enabled: false,
  instructions: '',
  incentives: '',
  compensationType: 'none',
  compensationScope: 'book',
  compensationAmount: 0,
  maxBetaReaders: null,
  sessionStartsAt: null,
  sessionEndsAt: null,
};

const COMPENSATION_TYPES = new Set<BetaReadCompensationType>(['none', 'points', 'payment']);
const COMPENSATION_SCOPES = new Set<BetaReadCompensationScope>(['book', 'chapter']);

export function isBetaReadCompensationType(v: string): v is BetaReadCompensationType {
  return COMPENSATION_TYPES.has(v as BetaReadCompensationType);
}

export function isBetaReadCompensationScope(v: string): v is BetaReadCompensationScope {
  return COMPENSATION_SCOPES.has(v as BetaReadCompensationScope);
}

/** Strip @ prefix and whitespace; returns null if empty after normalize. */
export function normalizeVenmoHandle(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let s = raw.trim();
  if (!s) return null;
  if (s.startsWith('@')) s = s.slice(1).trim();
  if (!s) return null;
  if (!/^[a-zA-Z0-9_-]{1,30}$/.test(s)) return null;
  return s;
}

export function isPlausibleVenmoHandle(raw: string | null | undefined): boolean {
  return normalizeVenmoHandle(raw) !== null;
}

export function parseCompensationAmount(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.max(0, Math.floor(raw));
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return 0;
}

function parseOptionalIsoDate(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && Number.isFinite(raw.getTime())) {
    return raw.toISOString();
  }
  if (typeof raw === 'string' && raw.trim()) {
    const d = new Date(raw.trim());
    if (Number.isFinite(d.getTime())) return d.toISOString();
  }
  return null;
}

export function parseBookBetaReadProgramInput(body: unknown): BookBetaReadProgramConfig {
  const b = body && typeof body === 'object' && !Array.isArray(body) ? (body as Record<string, unknown>) : {};

  const compensationType =
    typeof b.compensationType === 'string' && isBetaReadCompensationType(b.compensationType)
      ? b.compensationType
      : DEFAULT_BETA_READ_PROGRAM.compensationType;

  const compensationScope =
    typeof b.compensationScope === 'string' && isBetaReadCompensationScope(b.compensationScope)
      ? b.compensationScope
      : DEFAULT_BETA_READ_PROGRAM.compensationScope;

  let compensationAmount = parseCompensationAmount(b.compensationAmount);
  if (compensationType === 'payment') {
    compensationAmount = Math.min(compensationAmount, 1_000_000);
  } else if (compensationType === 'points') {
    compensationAmount = Math.min(compensationAmount, 1_000_000);
  } else {
    compensationAmount = 0;
  }

  let maxBetaReaders: number | null = null;
  const rawMax = b.maxBetaReaders;
  if (typeof rawMax === 'number' && Number.isFinite(rawMax) && rawMax > 0) {
    maxBetaReaders = Math.min(Math.floor(rawMax), 100_000);
  } else if (typeof rawMax === 'string' && rawMax.trim()) {
    const n = parseInt(rawMax, 10);
    if (Number.isFinite(n) && n > 0) maxBetaReaders = Math.min(n, 100_000);
  }

  const sessionStartsAt = parseOptionalIsoDate(b.sessionStartsAt);
  const sessionEndsAt = parseOptionalIsoDate(b.sessionEndsAt);

  return {
    enabled: b.enabled === true,
    instructions: typeof b.instructions === 'string' ? b.instructions.trim().slice(0, 20_000) : '',
    incentives: typeof b.incentives === 'string' ? b.incentives.trim().slice(0, 10_000) : '',
    compensationType,
    compensationScope,
    compensationAmount,
    maxBetaReaders,
    sessionStartsAt,
    sessionEndsAt,
  };
}

export function formatPaymentUsd(cents: number): string {
  const dollars = cents / 100;
  if (Number.isInteger(dollars)) return `$${dollars}`;
  return `$${dollars.toFixed(2)}`;
}

export function formatCompensationSummary(program: BookBetaReadProgramConfig): string | null {
  if (program.compensationType === 'none' || program.compensationAmount <= 0) {
    return null;
  }

  const scopeLabel = program.compensationScope === 'chapter' ? 'per chapter' : 'for the full book';

  if (program.compensationType === 'points') {
    const pts = program.compensationAmount.toLocaleString();
    return `${pts} point${program.compensationAmount === 1 ? '' : 's'} ${scopeLabel}`;
  }

  if (program.compensationType === 'payment') {
    return `${formatPaymentUsd(program.compensationAmount)} via Venmo ${scopeLabel}`;
  }

  return null;
}

export function programRequiresVenmo(program: BookBetaReadProgramConfig): boolean {
  return program.compensationType === 'payment';
}

export function programOffersQualificationRewards(program: BookBetaReadProgramConfig): boolean {
  return (
    (program.compensationType === 'points' || program.compensationType === 'payment') &&
    program.compensationAmount > 0
  );
}

export function serializeBetaReadProgramRow(row: {
  enabled: boolean;
  instructions: string;
  incentives: string;
  compensationType: string;
  compensationScope: string;
  compensationAmount: number;
  maxBetaReaders: number | null;
  sessionStartsAt?: Date | string | null;
  sessionEndsAt?: Date | string | null;
}): BookBetaReadProgramConfig {
  return {
    enabled: row.enabled,
    instructions: row.instructions,
    incentives: row.incentives,
    compensationType: isBetaReadCompensationType(row.compensationType)
      ? row.compensationType
      : 'none',
    compensationScope: isBetaReadCompensationScope(row.compensationScope)
      ? row.compensationScope
      : 'book',
    compensationAmount: row.compensationAmount,
    maxBetaReaders: row.maxBetaReaders ?? null,
    sessionStartsAt: parseOptionalIsoDate(row.sessionStartsAt ?? null),
    sessionEndsAt: parseOptionalIsoDate(row.sessionEndsAt ?? null),
  };
}

/** Convert config ISO dates to Date | null for Prisma writes. */
export function betaReadProgramConfigToDb(config: BookBetaReadProgramConfig): {
  enabled: boolean;
  instructions: string;
  incentives: string;
  compensationType: BetaReadCompensationType;
  compensationScope: BetaReadCompensationScope;
  compensationAmount: number;
  maxBetaReaders: number | null;
  sessionStartsAt: Date | null;
  sessionEndsAt: Date | null;
} {
  return {
    enabled: config.enabled,
    instructions: config.instructions,
    incentives: config.incentives,
    compensationType: config.compensationType,
    compensationScope: config.compensationScope,
    compensationAmount: config.compensationAmount,
    maxBetaReaders: config.maxBetaReaders,
    sessionStartsAt: config.sessionStartsAt ? new Date(config.sessionStartsAt) : null,
    sessionEndsAt: config.sessionEndsAt ? new Date(config.sessionEndsAt) : null,
  };
}
